/**
 * Recognising a handheld barcode reader from the keystrokes it sends.
 *
 * A Galileo — like nearly every fixed or handheld scanner in its default
 * mode — is a **keyboard wedge**: the operating system sees a second keyboard,
 * and a scan arrives as the payload typed character by character, usually
 * followed by Enter. There is no device to open, no permission to grant and no
 * API to call. The whole job is telling that burst apart from a person typing.
 *
 * That matters on the shop floor because the camera is the wrong instrument
 * there. A worker at a shared terminal has no camera pointed at them, a phone
 * in a glove is awkward, and the plant already owns readers mounted at the
 * machines. The badge is the same badge either way — this is a second way to
 * read it.
 *
 * ## How the burst is told apart
 *
 * Speed, and only speed. A scanner emits characters 5–20 ms apart and holds
 * that rate for the whole payload; a person types with gaps an order of
 * magnitude longer and irregular. So a run of characters whose every gap is
 * under {@link DEFAULT_MAX_GAP_MS} is a machine, and any slower keystroke
 * starts the buffer over.
 *
 * Sustaining 45 ms between keystrokes is roughly 260 words a minute, for
 * twenty-plus unbroken characters. Nobody does that by accident, and the
 * payload check on the way out means that even if they did, nothing would
 * happen unless what they typed also looked like a badge token.
 *
 * ## What it deliberately does not do
 *
 * **It never suppresses the characters themselves.** The obvious design —
 * swallow keystrokes once the burst looks mechanical — cannot know it is a scan
 * until the burst ends, so it has to guess partway through and eats real
 * keystrokes from anybody who types quickly. Here the characters land wherever
 * focus is, exactly as they would from a keyboard, and the *caller* clears the
 * field once a scan is confirmed. The single event worth swallowing is the
 * terminating Enter, which is known to be part of a scan at the moment it
 * arrives — and without that the reader would submit the sign-in form with a
 * badge token in the employee-code box.
 */

/** Above this gap between characters, the burst is a person and the buffer restarts. */
export const DEFAULT_MAX_GAP_MS = 45

/**
 * Shortest burst worth calling a scan.
 *
 * Deliberately tiny, and it was wrong at 20 for a week. A badge token is 48
 * characters, so 20 looked like a safe floor — but the card most of this plant
 * actually carries holds an *employee code*, and `demo-worker` is eleven
 * characters while a works number like `1042` is four. A threshold picked for
 * the long payload silently made the short one impossible.
 *
 * Three other things do the discriminating, which is what lets this be so low:
 * every gap must be under {@link DEFAULT_MAX_GAP_MS}, auto-repeat is excluded
 * (see `press`), and the payload still has to be read as *something* by
 * `badgeTokenFrom` or `employeeCodeFrom`. This only keeps a two-key stutter
 * from being considered at all.
 */
export const DEFAULT_MIN_LENGTH = 3

/**
 * Silence after which a payload is treated as finished without an Enter.
 *
 * Not every reader is configured to send a suffix, and a plant that has already
 * mounted its scanners is not going to reconfigure them because an application
 * insisted. Long enough to be certain the burst has stopped, short enough that
 * a worker does not notice waiting.
 */
export const DEFAULT_IDLE_MS = 120

export type ScanEvent =
  /** Not part of any burst — a modifier chord, a navigation key, a slow keystroke. */
  | { type: 'ignored' }
  /** Part of a burst that has not finished yet. */
  | { type: 'buffering' }
  /** A finished burst. The caller decides whether the text is a badge. */
  | { type: 'scan'; text: string }
  /** Enter arrived, but nothing mechanical preceded it — let the form have it. */
  | { type: 'enter' }

export interface BadgeScanner {
  /**
   * @param repeat  The key is being held down and the OS is repeating it.
   *                Auto-repeat fires around 30 ms apart, which is squarely
   *                inside scanner territory — without excluding it, leaning on
   *                a key would be read as a scan.
   */
  press(key: string, at: number, repeat?: boolean): ScanEvent
  /** Finish a burst that stopped without an Enter. Call after `DEFAULT_IDLE_MS`. */
  idle(at: number): ScanEvent
  /** Whether a burst is in progress, so the caller knows to arm its idle timer. */
  pending(): boolean
}

export interface BadgeScannerOptions {
  maxGapMs?: number
  minLength?: number
  idleMs?: number
}

export function createBadgeScanner(options: BadgeScannerOptions = {}): BadgeScanner {
  const maxGap = options.maxGapMs ?? DEFAULT_MAX_GAP_MS
  const minLength = options.minLength ?? DEFAULT_MIN_LENGTH
  const idleAfter = options.idleMs ?? DEFAULT_IDLE_MS

  let buffer = ''
  let lastAt = 0

  const reset = () => {
    buffer = ''
    lastAt = 0
  }

  const finish = (): ScanEvent => {
    const text = buffer
    reset()

    return text.length >= minLength ? { type: 'scan', text } : { type: 'enter' }
  }

  return {
    press(key, at, repeat = false) {
      /*
       * A wedge sends nothing but the payload: no arrows, no function keys, no
       * chords. `key.length === 1` is what separates a printable character from
       * every named key, and it holds for the whole printable range rather than
       * an alphabet somebody has to keep extending.
       */
      if (key === 'Enter' || key === 'Tab') {
        return buffer.length > 0 ? finish() : { type: 'enter' }
      }

      if (key.length !== 1) {
        // A named key in the middle of a burst means it was not a scan.
        reset()

        return { type: 'ignored' }
      }

      /*
       * A held key. No reader repeats — a scan is one pass over the symbol — so
       * this is a person leaning on the keyboard, and the burst it was part of
       * was never mechanical.
       */
      if (repeat) {
        reset()

        return { type: 'ignored' }
      }

      // Too slow to be mechanical: this keystroke starts a new burst rather
      // than extending the old one, so a person typing never accumulates.
      if (buffer.length > 0 && at - lastAt > maxGap) {
        buffer = ''
      }

      buffer += key
      lastAt = at

      return { type: 'buffering' }
    },

    idle(at) {
      if (buffer.length === 0 || at - lastAt < idleAfter) {
        return { type: 'ignored' }
      }

      const event = finish()

      // A burst that stopped short of the threshold is somebody typing; it must
      // not surface as an Enter nobody pressed.
      return event.type === 'scan' ? event : { type: 'ignored' }
    },

    pending() {
      return buffer.length > 0
    },
  }
}

/**
 * The badge token inside whatever the reader sent, or null.
 *
 * The card's QR holds `{FRONTEND_URL}/prijava/kartica/{token}` — a whole URL,
 * so that a phone camera opens the app directly — and a wedge types all of it.
 * The token is the last path segment either way, which is the same rule the
 * camera path uses.
 *
 * **Returning null is the safety.** Everything else on this screen is left
 * alone: a product barcode scanned at the terminal by mistake, a colleague's
 * pallet label, somebody's fast typing. Nothing is cleared and no request is
 * made unless what arrived actually looks like a badge, so the reader can never
 * hijack a sign-in that was already underway.
 *
 * The shape is the server's: `Str::lower(Str::random(48))`, so lowercase
 * alphanumerics. The range is loose around that rather than pinned to 48 —
 * a token length is the server's business to change, and a login screen that
 * silently stops recognising cards after such a change would be a miserable
 * thing to diagnose.
 */
export function badgeTokenFrom(scanned: string): string | null {
  const trimmed = scanned.trim()

  if (!trimmed) return null

  const candidate = trimmed.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() ?? ''

  return /^[a-z0-9]{24,64}$/i.test(candidate) ? candidate : null
}

/**
 * An employee code out of a scan that is not a KaproLean badge.
 *
 * Livprom's workers already carry a card. `korisnici.barkod` in the MES is
 * their employee code here — see MES-INTEGRATION.md, where it is described as
 * "this is how they sign in" — and 65 of 65 people have one. Those cards are
 * in pockets today, years before anybody prints a KaproLean badge, and a login
 * screen that ignored them would be ignoring the scanner the plant actually
 * owns.
 *
 * So a scan that is not a badge is offered to the employee-code box. That is a
 * *username*, not a credential: it is printed on the card, it is on the badge
 * the person is wearing, and putting it in a text field commits to nothing —
 * the password is still required. Being generous here costs nothing and being
 * strict would mean guessing at a format the MES does not constrain
 * (`employee_code` is a free string, and their barcodes are theirs to change).
 *
 * What is refused is anything that cannot be a code rather than anything that
 * does not look like one:
 *
 * - **URLs**, so a badge whose token failed the check above is not retyped into
 *   the username box, where a bearer credential has no business being.
 * - **Whitespace**, which no barcode symbology emits inside a payload and which
 *   marks the text as something else entirely.
 * - **Anything past the column width.** `users.employee_code` is
 *   `varchar(100)`; a longer scan is a label, not a person.
 */
export function employeeCodeFrom(scanned: string): string | null {
  const text = scanned.trim()

  if (text.length < 2 || text.length > 100) return null
  if (/\s/.test(text)) return null
  if (text.includes('/') || text.includes('\\')) return null

  return text
}
